import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import type { PromoDrillParams } from '@/features/promotion/types/promotion.types';
import { downloadBlob } from '@/utils/download';
import {
  DEFAULT_MP_PAGE_SIZE,
  SOURCE_INSTITUTE_MIN_SEARCH_LENGTH,
} from '../constants/marketPromotion.constants';
import {
  mpAdjustmentResponseSchema,
  mpApprovalResponseSchema,
  misCallerListSchema,
  mpApprovalDetailSchema,
  mpRegionDonationResponseSchema,
  mpRegionProjectReportSchema,
  mpRegionReportSchema,
  mpSummaryResponseSchema,
  sourceInstituteListSchema,
  type AmendDonationPayload,
  type DecideDonationPayload,
  type MisCaller,
  type MpAdjustmentResponse,
  type MpAdjustmentRow,
  type MpApprovalDetail,
  type MpApprovalResponse,
  type MpApprovalSearch,
  type MpRegionDonationParams,
  type MpRegionDonationResponse,
  type MpRegionFilterSearch,
  type MpRegionProjectReport,
  type MpRegionReport,
  type MpReportSearch,
  type MpSummaryResponse,
  type RecommendDonationPayload,
  type RejectRecommendationPayload,
  type SourceInstitute,
  type VerifyMisPayload,
} from '../types/marketPromotion.types';

export const marketPromotionQueryKeys = {
  all: () => ['market-promotion'] as const,
  adjustment: (params: MpReportSearch) =>
    [...marketPromotionQueryKeys.all(), 'adjustment', params] as const,
  distributedItem: (promoItemId: number, params: PromoDrillParams) =>
    [...marketPromotionQueryKeys.all(), 'distributed-item', params, promoItemId] as const,
  summary: (params: MpReportSearch) =>
    [...marketPromotionQueryKeys.all(), 'summary', params] as const,
  regions: (params: MpRegionFilterSearch) =>
    [...marketPromotionQueryKeys.all(), 'regions', params] as const,
  regionProjects: (params: MpRegionFilterSearch) =>
    [...marketPromotionQueryKeys.all(), 'region-projects', params] as const,
  regionDonations: (params: MpRegionDonationParams) =>
    [...marketPromotionQueryKeys.all(), 'region-donations', params] as const,
  approvals: (params: MpApprovalSearch, mis: boolean) =>
    [...marketPromotionQueryKeys.all(), 'approvals', mis, params] as const,
  approval: (detailId: number) =>
    [...marketPromotionQueryKeys.all(), 'approval', detailId] as const,
  misCallers: () => [...marketPromotionQueryKeys.all(), 'mis-callers'] as const,
  sourceInstitutes: (search: string) =>
    [...marketPromotionQueryKeys.all(), 'source-institutes', search] as const,
};

export const mpApprovalQueryOptions = (detailId: number) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.approval(detailId),
    queryFn: async (): Promise<MpApprovalDetail> => {
      const res = await apiClient.get(API.marketPromotion.approval(detailId));
      return mpApprovalDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const mpAdjustmentInfiniteQueryOptions = (params: MpReportSearch) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.adjustment(params),
    queryFn: async ({ pageParam }): Promise<MpAdjustmentResponse> => {
      const res = await apiClient.get(API.marketPromotion.adjustment, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return mpAdjustmentResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const distributedMpItemQueryOptions = (promoItemId: number, params: PromoDrillParams) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.distributedItem(promoItemId, params),
    queryFn: async (): Promise<MpAdjustmentRow | null> => {
      const res = await apiClient.get(API.marketPromotion.adjustment, {
        params: { ...params, promoItemId, page: FIRST_PAGE, pageSize: 1 },
      });
      return mpAdjustmentResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const mpSummaryInfiniteQueryOptions = (params: MpReportSearch) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.summary(params),
    queryFn: async ({ pageParam }): Promise<MpSummaryResponse> => {
      const res = await apiClient.get(API.marketPromotion.distributionSummary, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return mpSummaryResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const mpRegionsQueryOptions = (params: MpRegionFilterSearch) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.regions(params),
    queryFn: async (): Promise<MpRegionReport> => {
      const res = await apiClient.get(API.marketPromotion.regionSummary, { params });
      return mpRegionReportSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const mpRegionProjectsQueryOptions = (params: MpRegionFilterSearch) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.regionProjects(params),
    queryFn: async (): Promise<MpRegionProjectReport> => {
      const res = await apiClient.get(API.marketPromotion.regionProjects, { params });
      return mpRegionProjectReportSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const mpRegionDonationsInfiniteQueryOptions = (params: MpRegionDonationParams) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.regionDonations(params),
    queryFn: async ({ pageParam }): Promise<MpRegionDonationResponse> => {
      const res = await apiClient.get(API.marketPromotion.regionDonations, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return mpRegionDonationResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const mpApprovalsInfiniteQueryOptions = (params: MpApprovalSearch, mis: boolean) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.approvals(params, mis),
    queryFn: async ({ pageParam }): Promise<MpApprovalResponse> => {
      const res = await apiClient.get(
        mis ? API.marketPromotion.misApprovals : API.marketPromotion.approvals,
        { params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE } },
      );
      return mpApprovalResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function decideDonation(
  detailId: number,
  payload: DecideDonationPayload,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.decide(detailId), payload);
}

export async function recommendDonation(
  detailId: number,
  payload: RecommendDonationPayload,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.recommend(detailId), payload);
}

export async function rejectRecommendation(
  detailId: number,
  payload: RejectRecommendationPayload,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.rejectRecommendation(detailId), payload);
}

export async function amendDonation(
  detailId: number,
  payload: AmendDonationPayload,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.amend(detailId), payload);
}

export async function verifyMis(detailId: number, payload: VerifyMisPayload): Promise<void> {
  await apiClient.patch(API.marketPromotion.verifyMis(detailId), payload);
}

const ISO_DATE_LENGTH = 10;
const stamp = (): string => new Date().toISOString().slice(0, ISO_DATE_LENGTH);

export async function exportMpApprovals(params: MpApprovalSearch, mis: boolean): Promise<void> {
  const res = await apiClient.get(
    mis ? API.marketPromotion.misApprovalsExport : API.marketPromotion.approvalsExport,
    { params, responseType: 'blob' },
  );
  const fileName = mis ? 'market-promotion-mis-approval' : 'market-promotion-approval';
  downloadBlob(res.data as Blob, `${fileName}-${stamp()}.xlsx`);
}

export const misCallersQueryOptions = () =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.misCallers(),
    queryFn: async (): Promise<MisCaller[]> => {
      const res = await apiClient.get(API.marketPromotion.misCallers);
      return misCallerListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const sourceInstitutesQueryOptions = (search: string) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.sourceInstitutes(search),
    queryFn: async (): Promise<SourceInstitute[]> => {
      const res = await apiClient.get(API.marketPromotion.misSourceInstitutes, {
        params: { search },
      });
      return sourceInstituteListSchema.parse(res.data);
    },
    enabled: search.length >= SOURCE_INSTITUTE_MIN_SEARCH_LENGTH,
    staleTime: STALE_TIME,
  });
